import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Loader2, Sparkles, ListChecks } from 'lucide-react';

/**
 * Left column of the Sermon Prep tool — topic input + AI biblical research
 * results (key verses, concordance, dictionary, themes, application points,
 * cross references).
 */
const SermonResearchPanel = ({
  language,
  sermonTopic,
  setSermonTopic,
  sermonNotes,
  setSermonNotes,
  isLoading,
  onResearch,
  researchResults,
  onBuildOutline,
}) => {
  const { t } = useLanguage();
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            {t('sermon.sermonTopic')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            value={sermonTopic}
            onChange={(e) => setSermonTopic(e.target.value)}
            placeholder={t('sermon.topicPlaceholder')}
            className="text-base"
          />
          <Textarea
            value={sermonNotes}
            onChange={(e) => setSermonNotes(e.target.value)}
            placeholder={t('sermon.notesPlaceholder')}
            rows={3}
            className="text-sm"
          />
          <Button
            onClick={onResearch}
            disabled={isLoading || !sermonTopic.trim()}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('sermon.researching')}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {t('sermon.researchTopic')}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {researchResults && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-lg">
                {t('sermon.researchResults')}
              </CardTitle>
              {onBuildOutline && (
                <Button
                  onClick={onBuildOutline}
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="build-outline-from-research-btn"
                >
                  <ListChecks className="w-4 h-4 mr-1.5" />
                  {t('sermon.buildOutline')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {researchResults.key_verses?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-purple-600 mb-3 flex items-center gap-2">
                  <span>📖</span>
                  {t('sermon.keyVerses')}
                </h3>
                <div className="space-y-3">
                  {researchResults.key_verses.map((verse, idx) => (
                    <div key={idx} className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-l-4 border-purple-600">
                      <p className="text-xs font-semibold text-purple-700 dark:text-purple-400 mb-1">
                        {verse.reference} ({verse.translation})
                      </p>
                      <p className="text-sm text-slate-700 dark:text-slate-300 italic mb-2">
                        "{verse.text}"
                      </p>
                      {verse.relevance && (
                        <p className="text-xs text-slate-600 dark:text-slate-400">
                          <span className="font-semibold">Relevance:</span> {verse.relevance}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {researchResults.concordance?.word_occurrences?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-blue-600 mb-3 flex items-center gap-2">
                  <span>📚</span>
                  {t('sermon.concordance')}
                </h3>
                <div className="space-y-4">
                  {researchResults.concordance.word_occurrences.map((wordData, idx) => (
                    <div key={idx} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-bold text-blue-800 dark:text-blue-300 capitalize">
                          {wordData.word}
                        </h4>
                        {wordData.hebrew_greek && (
                          <span className="text-xs text-blue-600 dark:text-blue-400 italic">
                            {wordData.hebrew_greek}
                          </span>
                        )}
                      </div>
                      {wordData.definition && (
                        <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">
                          <span className="font-semibold">Definition:</span> {wordData.definition}
                        </p>
                      )}
                      {wordData.key_references?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                            Key References:
                          </span>
                          {wordData.key_references.map((ref, i) => (
                            <span key={i} className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                              {ref}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {researchResults.biblical_dictionary?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-green-600 mb-3 flex items-center gap-2">
                  <span>📕</span>
                  {t('sermon.biblicalDictionary')}
                </h3>
                <div className="space-y-4">
                  {researchResults.biblical_dictionary.map((entry, idx) => (
                    <div key={idx} className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <h4 className="font-bold text-green-800 dark:text-green-300 text-base mb-2">
                        {entry.term}
                      </h4>
                      {entry.definition && (
                        <p className="text-sm text-slate-700 dark:text-slate-300 mb-3">
                          {entry.definition}
                        </p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        {entry.old_testament_usage && (
                          <div>
                            <p className="font-semibold text-green-700 dark:text-green-400 mb-1">
                              Old Testament:
                            </p>
                            <p className="text-slate-600 dark:text-slate-400">
                              {entry.old_testament_usage}
                            </p>
                          </div>
                        )}
                        {entry.new_testament_usage && (
                          <div>
                            <p className="font-semibold text-green-700 dark:text-green-400 mb-1">
                              New Testament:
                            </p>
                            <p className="text-slate-600 dark:text-slate-400">
                              {entry.new_testament_usage}
                            </p>
                          </div>
                        )}
                      </div>
                      {entry.theological_significance && (
                        <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800">
                          <p className="font-semibold text-green-700 dark:text-green-400 text-xs mb-1">
                            Theological Significance:
                          </p>
                          <p className="text-xs text-slate-600 dark:text-slate-400">
                            {entry.theological_significance}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {researchResults.themes?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-orange-600 mb-3 flex items-center gap-2">
                  <span>🎯</span>
                  {t('sermon.theologicalThemes')}
                </h3>
                <div className="space-y-3">
                  {researchResults.themes.map((themeData, idx) => (
                    <div key={idx} className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      <h4 className="font-semibold text-orange-800 dark:text-orange-300 mb-2">
                        {themeData.theme || themeData}
                      </h4>
                      {themeData.explanation && (
                        <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">
                          {themeData.explanation}
                        </p>
                      )}
                      {themeData.supporting_verses?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {themeData.supporting_verses.map((verse, i) => (
                            <span key={i} className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 px-2 py-0.5 rounded">
                              {verse}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {researchResults.application?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-indigo-600 mb-3 flex items-center gap-2">
                  <span>🙏</span>
                  {t('sermon.applicationPoints')}
                </h3>
                <ul className="space-y-2">
                  {researchResults.application.map((point, idx) => (
                    <li key={idx} className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2 p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded">
                      <span className="text-indigo-600 font-bold mt-0.5">{idx + 1}.</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {researchResults.cross_references?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-pink-600 mb-2 flex items-center gap-2">
                  <span>🔗</span>
                  {t('sermon.crossReferences')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {researchResults.cross_references.map((ref, idx) => (
                    <span key={idx} className="text-xs bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-200 px-3 py-1 rounded-full">
                      {ref.reference || ref}
                      {ref.connection && ` - ${ref.connection}`}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SermonResearchPanel;
